/**
 * Avatar configuration for Poker Reborn
 *
 * Maps avatar IDs (1–43) to asset paths. Goga's illustrated characters.
 *
 * @see studio/avatars/README.md - Pipeline overview
 * @see tasks/active/T32_AVATAR_RESEARCH.md - Avatar system research
 */

import { ASSET_PATHS, isRetinaDisplay } from '@/config/assets';

// =============================================================================
// CONSTANTS
// =============================================================================

/** Total number of available avatars */
export const AVATAR_COUNT = 43;

/** Default avatar ID (used as fallback) */
export const DEFAULT_AVATAR_ID = 1;

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Get the asset path for an avatar by ID.
 * Automatically selects 1x or 2x resolution based on display.
 */
export function getAvatarPath(avatarId: number): string {
  const resolution = isRetinaDisplay ? '2x' : '1x';
  const paddedId = String(avatarId).padStart(2, '0');
  return `${ASSET_PATHS.images}/avatars/${resolution}/avatar_${paddedId}.png`;
}

/**
 * Type guard: checks if a value is a valid avatar ID (integer 1–43).
 */
export function isValidAvatarId(id: unknown): id is number {
  return typeof id === 'number' && Number.isInteger(id) && id >= 1 && id <= AVATAR_COUNT;
}

/**
 * Get all avatar IDs as an array [1, 2, ..., 43].
 */
export function getAllAvatarIds(): number[] {
  return Array.from({ length: AVATAR_COUNT }, (_, i) => i + 1);
}
